import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Alert,
  Stack,
  Divider,
  Typography,
  Card,
  CardContent,
  Skeleton,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useParams, useNavigate } from 'react-router-dom';
import { habitService } from '../services/api';
import { getCategoryHexColor } from '../utils/habitColors';
import { ReportRequest, ReportData, HabitDefinition, HabitSummary, HabitVersionInfo } from '../types/api';
import LoadingSpinner from './LoadingSpinner';
import ReportControls from './ReportControls';
import ChartCard from './ChartCard';
import HabitCard from './HabitCard';
import HabitDetailModal from './HabitDetailModal';
import AddHabitGridButton from './AddHabitGridButton';
import CreateHabitModal from './CreateHabitModal';

const ReportDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { habitId, childHabitId } = useParams<{ habitId: string; childHabitId?: string }>();
  const navigate = useNavigate();
  const activeHabitId = childHabitId || habitId;
  
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [reportHabitsWithChildrenById, setReportHabitsWithChildrenById] = useState<Record<string, HabitSummary>>({});
  const [categories, setCategories] = useState<string[]>([]);
  const [habitDefinitions, setHabitDefinitions] = useState<Record<string, HabitDefinition>>({});
  const [loading, setLoading] = useState(true);
  const [loadingHabitDefinitions, setLoadingHabitDefinitions] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  
  // Modal state
  const [selectedHabit, setSelectedHabit] = useState<HabitSummary | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [habitVersions, setHabitVersions] = useState<HabitVersionInfo[]>([]);
  const [habitWasUpdated, setHabitWasUpdated] = useState(false);
  const lastResolvedHabitIdRef = useRef<string | null>(null);
  
  // Derive modal open state ONLY from URL - single source of truth
  const modalOpen = !!habitId;
  
  // Create habit modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  
  // Report filters
  const [weeks, setWeeks] = useState<number>(1);
  const [selectedCategory, setSelectedCategory] = useState<string>('All Categories');
  const [reportType, setReportType] = useState<string>('default');
  const [sortBy, setSortBy] = useState<string>('alphabetical');
  const [categoryGoalTypeFilter, setCategoryGoalTypeFilter] = useState<string>('all');

  // Debounced hover state for add button zone
  const [isHoverZoneExpanded, setIsHoverZoneExpanded] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const collapseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
    };
  }, []);

  // Debounced hover handlers
  const handleHoverZoneEnter = () => {
    // Clear any pending collapse
    if (collapseTimeoutRef.current) {
      clearTimeout(collapseTimeoutRef.current);
      collapseTimeoutRef.current = null;
    }

    // If already expanded or expanding, don't create new timeout
    if (isHoverZoneExpanded || hoverTimeoutRef.current) return;

    // Delay expansion by 100ms
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHoverZoneExpanded(true);
      hoverTimeoutRef.current = null;
    }, 100);
  };

  const handleHoverZoneLeave = () => {
    // Clear any pending expansion
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
      return;
    }

    // Delay collapse by 300ms (longer to prevent rapid bouncing)
    collapseTimeoutRef.current = setTimeout(() => {
      setIsHoverZoneExpanded(false);
      collapseTimeoutRef.current = null;
    }, 300);
  };

  const loadCategories = useCallback(async () => {
    try {
      const response = await habitService.getCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const loadHabitDefinitions = useCallback(async () => {
    try {
      const response = await habitService.getDefinitions();
      if (response.success && response.data) {
        setHabitDefinitions(response.data);
      }
    } catch (error) {
      console.error('Failed to load habit definitions:', error);
    } finally {
      setLoadingHabitDefinitions(false);
    }
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setAlert(null);

    try {
      const request: ReportRequest = {
        weeks,
        category: selectedCategory === 'All Categories' ? undefined : selectedCategory,
        type: reportType,
        sortBy,
      };

      const [response, responseWithChildren] = await Promise.all([
        habitService.getReport(request),
        habitService.getReport({ ...request, includeChildren: true }),
      ]);

      if (response.success && response.data) {
        const normalizedReportData = {
          ...response.data,
          habits: response.data.habits ?? [],
        };
        setReportData(normalizedReportData);

        const habitsForOffTrack = (responseWithChildren.success && responseWithChildren.data
          ? responseWithChildren.data.habits
          : response.data.habits) ?? [];
        const habitsById = habitsForOffTrack.reduce<Record<string, HabitSummary>>((acc, habit) => {
          acc[habit.habitId] = habit;
          return acc;
        }, {});
        setReportHabitsWithChildrenById(habitsById);
      } else {
        setReportHabitsWithChildrenById({});
        setAlert({ type: 'error', message: response.error || 'Failed to load report' });
      }
    } catch (error) {
      setReportHabitsWithChildrenById({});
      setAlert({ type: 'error', message: 'Failed to connect to server' });
    } finally {
      setLoading(false);
    }
  }, [weeks, selectedCategory, reportType, sortBy]);

  useEffect(() => {
    loadCategories();
    loadHabitDefinitions();
    loadReport();
  }, [loadCategories, loadHabitDefinitions, loadReport]);

  // Handle habitId from URL params - sync habit data with URL
  useEffect(() => {
    if (!activeHabitId) {
      if (selectedHabit) {
        setSelectedHabit(null);
        setHabitVersions([]);
        lastResolvedHabitIdRef.current = null;

        if (habitWasUpdated) {
          loadReport();
          loadHabitDefinitions();
          setHabitWasUpdated(false);
        }
      }
      return;
    }

    if (selectedHabit && selectedHabit.habitId === activeHabitId) {
      lastResolvedHabitIdRef.current = activeHabitId;
      return;
    }

    if (lastResolvedHabitIdRef.current === activeHabitId) {
      return;
    }

    if (!reportData) {
      return;
    }

    const reportHabits = reportData.habits ?? [];
    const habit = reportHabits.find(h => h.habitId === activeHabitId) || reportHabitsWithChildrenById[activeHabitId];

    if (habit) {
      lastResolvedHabitIdRef.current = activeHabitId;
      setSelectedHabit(habit);
      setModalLoading(true);
      setHabitVersions([]);

      habitService.getHabitVersions(activeHabitId)
        .then(response => {
          if (response.success && response.data) {
            setHabitVersions(response.data);
          } else {
            console.error('Failed to fetch habit versions:', response.error);
          }
        })
        .catch(error => {
          console.error('Error fetching habit versions:', error);
        })
        .finally(() => {
          setModalLoading(false);
        });
      return;
    }

    const loadHabitById = async () => {
      lastResolvedHabitIdRef.current = activeHabitId;
      setModalLoading(true);
      setHabitVersions([]);

      try {
        const habitResponse = await habitService.getHabit(activeHabitId);
        if (!habitResponse.success || !habitResponse.data) {
          navigate('/history/stats', { replace: true });
          return;
        }

        const definition = habitResponse.data;
        const fallbackHabit: HabitSummary = {
          habitId: definition.habitId,
          habitDisplayName: definition.habitDisplayName,
          goalAmountScaled: definition.goal.type === 'THRESHOLD' ? definition.goal.value : definition.goal.max,
          goalAmount: definition.goal.type === 'THRESHOLD' ? definition.goal.value : definition.goal.max,
          goalUnit: definition.goalUnit,
          currentAmountCompletedOverReportTimeInterval: 0,
          currentAmountCompletedOverGoalTimeInterval: 0,
          percentageCompletedOverReportTimeInterval: 0,
          percentageCompletedOverGoalTimeInterval: 0,
          category: definition.category,
          parentHabitId: definition.parentHabitId,
          entries: [],
        };

        setSelectedHabit(fallbackHabit);
        setHabitDefinitions(prev => ({ ...prev, [definition.habitId]: definition }));

        const versionsResponse = await habitService.getHabitVersions(activeHabitId);
        if (versionsResponse.success && versionsResponse.data) {
          setHabitVersions(versionsResponse.data);
        }
      } catch (error) {
        console.error('Error loading habit by URL id:', error);
        navigate('/history/stats', { replace: true });
      } finally {
        setModalLoading(false);
      }
    };

    void loadHabitById();
  }, [activeHabitId, reportData, reportHabitsWithChildrenById, navigate, selectedHabit, loadReport, loadHabitDefinitions, habitWasUpdated]);

  const handleRefresh = () => {
    loadReport();
  };

  const handleHabitClick = async (habit: HabitSummary) => {
    // Navigate to URL with habitId
    navigate(`/history/stats/${habit.habitId}`);
  };

  const getSelectedHabitParentId = (): string | undefined => {
    if (!selectedHabit) return undefined;
    return selectedHabit.parentHabitId || habitDefinitions[selectedHabit.habitId]?.parentHabitId;
  };

  const handleOpenHabitById = (targetHabitId: string) => {
    const parentHabitId = selectedHabit?.habitId && !selectedHabit.parentHabitId ? selectedHabit.habitId : (habitId || selectedHabit?.parentHabitId);
    const targetRoute = parentHabitId ? `/history/stats/${parentHabitId}/${targetHabitId}` : `/history/stats/${targetHabitId}`;
    navigate(targetRoute);
  };

  const handleModalClose = () => {
    if (childHabitId && habitId) {
      navigate(`/history/stats/${habitId}`);
      return;
    }

    const parentHabitId = getSelectedHabitParentId();
    if (parentHabitId) {
      navigate(`/history/stats/${parentHabitId}`);
      return;
    }

    navigate('/history/stats');
  };

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  const handleHabitCreated = () => {
    // Refresh the report after creating a new habit
    loadReport();
    loadHabitDefinitions();
  };

  const handleHabitDeleted = () => {
    // Close the modal and reload the full report so the deleted card disappears
    handleModalClose();
    loadReport();
    loadHabitDefinitions();
  };

  const handleHabitUpdated = async () => {
    // Refetch only the specific habit's data after successful update
    if (selectedHabit) {
      try {
        // Refetch habit versions for the modal
        const versionsResponse = await habitService.getHabitVersions(selectedHabit.habitId);
        if (versionsResponse.success && versionsResponse.data) {
          setHabitVersions(versionsResponse.data);
          
          // Update just this habit's definition in the definitions map
          const latestVersion = versionsResponse.data.find(v => v.isLatestPublishedVersion);
          if (latestVersion) {
            setHabitDefinitions(prev => ({
              ...prev,
              [selectedHabit.habitId]: latestVersion.habitDefinition
            }));
          }
        }

        // Mark that habit was updated, but defer report refresh until modal closes
        setHabitWasUpdated(true);
      } catch (error) {
        console.error('Error refetching habit data:', error);
      }
    }
  };

  const getPublishedHabitsByGoalType = (
    goalType?: 'THRESHOLD' | 'RANGE' | 'AVERAGE',
    category?: string,
  ) => {
    const habits = reportData?.habits ?? [];
    return habits.filter(habit => {
      const definition = habitDefinitions[habit.habitId];
      const isPublished = definition && !definition.isDraft;
      const matchesCategory = !category || habit.category === category;

      if (!goalType) return isPublished && matchesCategory;
      return isPublished && matchesCategory && definition.goal.type === goalType;
    });
  };

  const getHabitTooltipText = (habit: HabitSummary): string => {
    const definition = habitDefinitions[habit.habitId];
    if (!definition) return '';

    const formatNumber = (num: number) => num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
    const formatAmount = (num: number) => {
      const formatted = formatNumber(num);
      return num < 0 ? `(${formatted})` : formatted;
    };

    if (definition.goal.type === 'RANGE' || definition.goal.type === 'AVERAGE') {
      if (definition.goal.type === 'AVERAGE' && !hasReportIntervalData(habit)) {
        return 'No data';
      }

      const { min, max } = definition.goal;
      const scaledMin = definition.goal.type === 'AVERAGE' ? min : min * weeks;
      const scaledMax = definition.goal.type === 'AVERAGE' ? max : max * weeks;
      const current = habit.currentAmountCompletedOverReportTimeInterval;
      
      const periodLabel = definition.goal.type === 'AVERAGE' ? 'average' : 'total';
      const timeText = weeks > 1 ? `(past ${weeks} weeks ${periodLabel})` : `(past week ${periodLabel})`;
      const prefix = definition.goal.type === 'AVERAGE' ? 'Average' : 'Progress';
      return `${prefix}: ${formatAmount(current)} / ${formatAmount(scaledMin)} – ${formatAmount(scaledMax)} ${habit.goalUnit} ${timeText}`;
    }

    const percentText = `${formatNumber(habit.percentageCompletedOverReportTimeInterval)}%`;
    return percentText;
  };

  const isRangeHabitOnTrack = (habit: HabitSummary): boolean => {
    const definition = habitDefinitions[habit.habitId];
    if (!definition || (definition.goal.type !== 'RANGE' && definition.goal.type !== 'AVERAGE')) return true;

    const { min, max } = definition.goal;
    const scaledMin = definition.goal.type === 'AVERAGE' ? min : min * weeks;
    const scaledMax = definition.goal.type === 'AVERAGE' ? max : max * weeks;
    const current = habit.currentAmountCompletedOverReportTimeInterval;

    return current >= scaledMin && current <= scaledMax;
  };

  const hasReportIntervalData = (habit: HabitSummary): boolean => {
    if (typeof habit.hasEntriesOverReportTimeInterval === 'boolean') {
      return habit.hasEntriesOverReportTimeInterval;
    }

    if (habit.entries && habit.entries.length > 0) {
      return true;
    }

    return habit.currentAmountCompletedOverReportTimeInterval !== 0;
  };

  const getRangeHabitStatus = (habit: HabitSummary): 'on-track' | 'off-track' | 'no-data' => {
    const definition = habitDefinitions[habit.habitId];
    if (!definition || (definition.goal.type !== 'RANGE' && definition.goal.type !== 'AVERAGE')) {
      return 'on-track';
    }

    if (definition.goal.type === 'AVERAGE' && !hasReportIntervalData(habit)) {
      return 'no-data';
    }

    return isRangeHabitOnTrack(habit) ? 'on-track' : 'off-track';
  };

  const getOffTrackChildHabitCount = (habit: HabitSummary): number => {
    const definition = habitDefinitions[habit.habitId];
    if (!definition?.childHabitIds?.length) return 0;

    const isParentGoalMet = definition.goal.type === 'THRESHOLD'
      ? habit.currentAmountCompletedOverReportTimeInterval >= definition.goal.value
      : habit.currentAmountCompletedOverReportTimeInterval >= definition.goal.min &&
        habit.currentAmountCompletedOverReportTimeInterval <= definition.goal.max;

    if (!isParentGoalMet) return 0;

    const childGoalNotMetCount = definition.childHabitIds.reduce((count, childHabitId) => {
      const childDefinition = habitDefinitions[childHabitId];
      if (!childDefinition) return count;

      const childHabit = reportHabitsWithChildrenById[childHabitId];
      const childCurrent = childHabit?.currentAmountCompletedOverReportTimeInterval ?? 0;
      const childGoalMet = childDefinition.goal.type === 'THRESHOLD'
        ? childCurrent >= childDefinition.goal.value
        : childCurrent >= childDefinition.goal.min && childCurrent <= childDefinition.goal.max;

      return childGoalMet ? count : count + 1;
    }, 0);

    return childGoalNotMetCount;
  };

  const prepareBarChartData = () => {
    if (!reportData) return null;

    const reportHabits = reportData.habits ?? [];
    if (reportHabits.length === 0) return null;

    // Filter for habits with THRESHOLD goal type only
    const publishedHabits = getPublishedHabitsByGoalType('THRESHOLD', 'Manage');
    if (publishedHabits.length === 0) return null;

    // Calculate available space per tick - balance readability with overflow prevention
    // Chart width varies by screen size but typically 400-800px
    // Use conservative estimate and account for actual margins (left: 60, right: 20)
    const estimatedChartWidth = isMobile ? 300 : (isTablet ? 500 : 600);
    const chartContentWidth = estimatedChartWidth - 80; // Subtract left/right margins
    const numTicks = publishedHabits.length;
    const tickSpacing = numTicks > 0 ? chartContentWidth / numTicks : chartContentWidth;
    void tickSpacing;

    // Keep actual completion for tooltip and a bounded visual value for bar height.
    const data = publishedHabits.map(habit => ({
      habit: habit.habitDisplayName,
      actualPercentage: habit.percentageCompletedOverReportTimeInterval,
      visualPercentage:
        habit.percentageCompletedOverReportTimeInterval > 100
          ? 110
          : habit.percentageCompletedOverReportTimeInterval,
    }));

    // Sort by completion percentage (highest to lowest)
    data.sort((a, b) => b.actualPercentage - a.actualPercentage);

    return {
      dataset: data,
      xAxis: [{ 
        scaleType: 'band' as const, 
        dataKey: 'habit',
        tickLabelStyle: {
          display: 'none',
        },
      }],
      yAxis: [{ 
        min: 0,
        max: 100,
        valueFormatter: (value: number) => `${value}%`
      }],
      series: [
        { 
          dataKey: 'visualPercentage', 
          label: 'Completion %',
          color: '#32CA1D',
          valueFormatter: (value: number | null, context: { dataIndex?: number }) => {
            if (value == null || context.dataIndex == null) return '';
            const actualValue = data[context.dataIndex]?.actualPercentage;
            if (actualValue == null) return '';
            // Helper to format numbers - show whole numbers without decimals
            const formatNumber = (num: number) => num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
            return `${formatNumber(actualValue)}%`;
          }
        },
      ],
    };
  };

  const prepareCategoryBarChartData = () => {
    if (!reportData) return null;

    const reportHabits = reportData.habits ?? [];
    if (reportHabits.length === 0) return null;

    // Filter out habits that are in draft (no published version)
    let publishedHabits = categoryGoalTypeFilter === 'threshold' 
      ? getPublishedHabitsByGoalType('THRESHOLD')
      : categoryGoalTypeFilter === 'range'
      ? getPublishedHabitsByGoalType('RANGE')
      : categoryGoalTypeFilter === 'average'
      ? getPublishedHabitsByGoalType('AVERAGE')
      : getPublishedHabitsByGoalType();

    // Group habits by category and calculate average percentage
    const categoryData: Record<string, { sum: number; count: number }> = {};
    
    publishedHabits.forEach(habit => {
      if (!categoryData[habit.category]) {
        categoryData[habit.category] = { sum: 0, count: 0 };
      }
      categoryData[habit.category].sum += habit.percentageCompletedOverReportTimeInterval;
      categoryData[habit.category].count += 1;
    });

    // Calculate averages and prepare chart data
    const categories = Object.entries(categoryData).map(([category, { sum, count }]) => ({
      category: category,
      percentage: Math.round((sum / count) * 10) / 10, // Average percentage with 1 decimal
    }));
    if (categories.length === 0) return null;

    // Sort by percentage (highest to lowest)
    categories.sort((a, b) => b.percentage - a.percentage);

    return {
      dataset: categories,
      xAxis: [{ 
        scaleType: 'band' as const, 
        dataKey: 'category',
        colorMap: {
          type: 'ordinal' as const,
          colors: categories.map(cat => getCategoryHexColor(cat.category)),
        }
      }],
      yAxis: [{ 
        valueFormatter: (value: number) => `${value}%`
      }],
      series: [
        { 
          dataKey: 'percentage', 
          label: 'Average Completion %',
          valueFormatter: (value: number | null) => {
            if (value == null) return '';
            // Helper to format numbers - show whole numbers without decimals
            const formatNumber = (num: number) => num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
            return `${formatNumber(value)}%`;
          }
        },
      ],
    };
  };

  if (loadingCategories) {
    return <LoadingSpinner />;
  }

  const showSkeletonLoaders = loading || loadingHabitDefinitions;

  const skeletonCardCount = isMobile ? 3 : isTablet ? 4 : 6;

  const handleSortByChange = (newSortBy: string) => {
    setSortBy(newSortBy);
    // loadReport will be called automatically via the useEffect that depends on it,
    // since sortBy is a dependency of the loadReport useCallback.
  };

  return (
    <Box>
      {/* Report Controls */}
      <ReportControls
        weeks={weeks}
        selectedCategory={selectedCategory}
        reportType={reportType}
        sortBy={sortBy}
        categories={categories}
        loading={showSkeletonLoaders}
        onWeeksChange={setWeeks}
        onCategoryChange={setSelectedCategory}
        onReportTypeChange={setReportType}
        onSortByChange={handleSortByChange}
        onRefresh={handleRefresh}
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Report Results */}
      {showSkeletonLoaders ? (
        <>
          <Box sx={{ mb: 3 }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Skeleton variant="text" width={220} height={34} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
                  <Box sx={{ mt: 2, display: 'flex', gap: 1.5 }}>
                    <Skeleton variant="rounded" width={96} height={34} />
                    <Skeleton variant="rounded" width={96} height={34} />
                    <Skeleton variant="rounded" width={96} height={34} />
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Skeleton variant="text" width={240} height={34} />
                    <Skeleton variant="rounded" width={140} height={28} />
                  </Box>
                  <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
                </CardContent>
              </Card>
            </Stack>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Card>
            <CardContent>
              <Skeleton variant="text" width={320} height={34} sx={{ mb: 2 }} />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                  gap: 3,
                }}
              >
                {Array.from({ length: skeletonCardCount }).map((_, index) => (
                  <Card key={`habit-card-skeleton-${index}`}>
                    <CardContent>
                      <Skeleton variant="text" width="72%" height={32} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="48%" height={26} sx={{ mb: 2 }} />
                      <Skeleton variant="rectangular" width="100%" height={72} sx={{ borderRadius: 2, mb: 2 }} />
                      <Skeleton variant="text" width="64%" height={24} />
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        </>
      ) : reportData && (
        <>
          {/* Charts Section */}
          <Box sx={{ mb: 3 }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
              {/* Bar Chart */}
              <ChartCard title="Habits by Effectiveness">
                {(() => {
                  const barData = prepareBarChartData();
                  const rangeHabits = [
                    ...getPublishedHabitsByGoalType('RANGE', 'Manage'),
                    ...getPublishedHabitsByGoalType('AVERAGE', 'Manage'),
                  ];
                  
                  return barData ? (
                    <>
                      <BarChart
                        dataset={barData.dataset}
                        xAxis={barData.xAxis}
                        yAxis={barData.yAxis}
                        series={barData.series}
                        height={300}
                        margin={{ left: 60, right: 20, top: 20, bottom: 40 }}
                      />
                      <Box sx={{ 
                        mt: 0.5, 
                        ml: 9.5, 
                        display: 'flex', 
                        gap: 1, 
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        pb: 1,
                        '&::-webkit-scrollbar': {
                          height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                          },
                        },
                      }}>
                        {rangeHabits.map((habit) => {
                          const status = getRangeHabitStatus(habit);
                          const isOnTrack = status === 'on-track';
                          const isNoData = status === 'no-data';
                          const statusLabel = isNoData ? 'No data' : isOnTrack ? 'On-Track' : 'Off-Track';
                          const chipColor = isNoData
                            ? theme.palette.grey[600]
                            : isOnTrack
                            ? '#32CA1D'
                            : theme.palette.error.main;
                          const chipHoverColor = isNoData
                            ? theme.palette.grey[500]
                            : isOnTrack
                            ? '#2ab619'
                            : theme.palette.error.dark;
                          
                          return (
                            <Tooltip 
                              key={habit.habitId} 
                              title={
                                <Box>
                                  <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                                    {habit.habitDisplayName}
                                  </Typography>
                                  <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.2)', mb: 1 }} />
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Box sx={{ 
                                      width: 12, 
                                      height: 12, 
                                      background: `linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%), ${chipColor}`,
                                      border: '1px solid rgba(255, 255, 255, 0.15)',
                                      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.25)',
                                      borderRadius: '2px',
                                    }} />
                                    <Typography sx={{ fontSize: '14px' }}>
                                      {statusLabel}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.2)', mb: 1 }} />
                                  <Typography sx={{ fontSize: '14px' }}>{getHabitTooltipText(habit)}</Typography>
                                </Box>
                              }
                              arrow
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: 'rgba(50, 50, 50, 0.95)',
                                    padding: '12px 16px',
                                    borderRadius: '4px',
                                    maxWidth: 500,
                                  }
                                }
                              }}
                            >
                              <Chip 
                                label={
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    minHeight: '60px',
                                    py: 0.5 
                                  }}>
                                    <Typography sx={{ 
                                      fontSize: '12px',
                                      textAlign: 'center',
                                      maxWidth: '100px',
                                      lineHeight: 1.2,
                                      wordWrap: 'break-word',
                                      overflowWrap: 'break-word',
                                      whiteSpace: 'normal',
                                    }}>
                                      {habit.habitDisplayName}
                                    </Typography>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap', textTransform: 'none', mt: 'auto' }}>
                                      {statusLabel}
                                    </Typography>
                                  </Box>
                                }
                                sx={{ 
                                  background: `linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%), ${chipColor}`,
                                  color: 'white',
                                  borderRadius: '6px',
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.25)',
                                  height: 'auto',
                                  '&:hover': {
                                    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%), ${chipHoverColor}`,
                                    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.25), inset 0 -1px 0 rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.3)',
                                  }
                                }} 
                              />
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </>
                  ) : (
                    <Typography color="text.secondary">No data available</Typography>
                  );
                })()}
              </ChartCard>

              {/* Category Bar Chart */}
              <ChartCard 
                title="Categories by Effectiveness"
                action={
                  <FormControl size="small" sx={{ minWidth: 200, m: 0 }}>
                    <InputLabel id="category-goal-type-filter-label">Goal Type</InputLabel>
                    <Select
                      labelId="category-goal-type-filter-label"
                      id="category-goal-type-filter"
                      value={categoryGoalTypeFilter}
                      label="Goal Type"
                      onChange={(e) => setCategoryGoalTypeFilter(e.target.value)}
                      sx={{ height: '32px' }}
                    >
                      <MenuItem value="all">All Goal Types</MenuItem>
                      <MenuItem value="threshold">Threshold Only</MenuItem>
                      <MenuItem value="range">Range Only</MenuItem>
                      <MenuItem value="average">Average Only</MenuItem>
                    </Select>
                  </FormControl>
                }
              >
                {(() => {
                  const categoryBarData = prepareCategoryBarChartData();
                  return categoryBarData ? (
                    <BarChart
                      dataset={categoryBarData.dataset}
                      xAxis={categoryBarData.xAxis}
                      yAxis={categoryBarData.yAxis}
                      series={categoryBarData.series}
                      height={300}
                      margin={{ left: 60, right: 20, top: 20, bottom: 40 }}
                      slotProps={{
                        legend: {
                          sx: {
                            "& .MuiChartsLegend-mark": {
                              display: "none",
                            },
                          },
                        },
                      }}
                    />
                  ) : (
                    <Typography color="text.secondary">No data available</Typography>
                  );
                })()}
              </ChartCard>
            </Stack>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Detailed Habits List */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detailed Progress - {reportData.period}
                {reportData.category !== 'All' && ` (${reportData.category})`}
              </Typography>

              {(reportData.habits ?? []).length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No habits found for the selected criteria
                </Typography>
              ) : (
                <>
                  <Box 
                    sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                      gap: 3 
                    }}
                  >
                    {(reportData.habits ?? []).map((habit) => (
                      <HabitCard
                        key={habit.habitId}
                        habit={habit}
                        habitDefinition={habitDefinitions[habit.habitId]}
                        onClick={handleHabitClick}
                        weeks={weeks}
                        offTrackChildHabitCount={getOffTrackChildHabitCount(habit)}
                      />
                    ))}
                    {/* Add button in grid if last row has space */}
                    {(() => {
                      const columnsPerRow = isMobile ? 1 : isTablet ? 2 : 3;
                      const habitCount = (reportData.habits ?? []).length;
                      const lastRowItemCount = habitCount % columnsPerRow;
                      const hasSpaceInLastRow = lastRowItemCount !== 0;
                      
                      if (hasSpaceInLastRow) {
                        return (
                          <Box
                            key="add-habit-button"
                            sx={{
                              position: 'relative',
                              minHeight: 200,
                            }}
                          >
                            <AddHabitGridButton onAddClick={handleOpenCreateModal} />
                          </Box>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                  
                  {/* Hover zone below grid if last row is full */}
                  {(() => {
                    const columnsPerRow = isMobile ? 1 : isTablet ? 2 : 3;
                    const habitCount = reportData.habits.length;
                    const lastRowItemCount = habitCount % columnsPerRow;
                    const isLastRowFull = lastRowItemCount === 0;
                    
                    if (isLastRowFull) {
                      return (
                        <Box
                          onMouseEnter={handleHoverZoneEnter}
                          onMouseLeave={handleHoverZoneLeave}
                          sx={{
                            height: isHoverZoneExpanded ? 200 : 10,
                            position: 'relative',
                            transition: 'height 0.2s ease-in-out',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: 200,
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                              gap: 3,
                            }}
                          >
                            <Box sx={{ position: 'relative' }}>
                              <AddHabitGridButton onAddClick={handleOpenCreateModal} />
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Habit Detail Modal */}
      <HabitDetailModal
        open={modalOpen}
        habit={selectedHabit}
        habitVersions={habitVersions}
        loading={modalLoading}
        isChildHabit={!!(selectedHabit?.parentHabitId || (selectedHabit && habitDefinitions[selectedHabit.habitId]?.parentHabitId))}
        onClose={handleModalClose}
        onHabitUpdated={handleHabitUpdated}
        onHabitDeleted={handleHabitDeleted}
        onOpenHabitById={handleOpenHabitById}
        onHabitReloadRequired={() => {
          if (!selectedHabit) return;
          setModalLoading(true);
          setHabitVersions([]);
          habitService.getHabitVersions(selectedHabit.habitId)
            .then(response => {
              if (response.success && response.data) {
                setHabitVersions(response.data);
                // Also refresh this habit's entry in the definitions map.
                const latest = response.data.find(v => v.isLatestPublishedVersion);
                if (latest) {
                  setSelectedHabit(prev => prev ? { ...prev, category: latest.habitDefinition.category } : prev);
                  setHabitDefinitions(prev => ({ ...prev, [selectedHabit.habitId]: latest.habitDefinition }));
                }
              }
            })
            .catch(err => console.error('Error reloading habit after category change:', err))
            .finally(() => setModalLoading(false));
        }}
      />

      {/* Create Habit Modal */}
      <CreateHabitModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onHabitCreated={handleHabitCreated}
      />
    </Box>
  );
};

export default ReportDashboard;